// C:\Users\USER\bookfete\frontend\src\components\home\HomePageLuxe.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../common/Icon';
import { listPrintFormats } from '../../services/ordersApi';
import { formatPriceCents } from '../../utils/orderWorkflow';
import '../../styles/luxe-theme.css';
import './HomeLuxe.css';

// Icone unique par evenement (maquette "Maquettes Bookipix", 2026-10-11) :
// remplace les 9 composants SVG multicolores precedents par le meme jeu au
// trait utilise partout ailleurs sur le site.
const EVENT_ICONS = {
  anniversaire: 'cake',
  retraite: 'clock',
  depart: 'exit',
  mariage: 'heart',
  naissance: 'moon',
  voyage: 'plane',
  finProjet: 'sparkle',
  famille: 'home',
  choixLibre: 'book',
};
const EVENT_ORDER = ['anniversaire', 'retraite', 'depart', 'mariage', 'naissance', 'voyage', 'finProjet', 'famille', 'choixLibre'];

const HomePageLuxe = () => {
  const { t, i18n } = useTranslation('home');
  const priceLocale = i18n.language === 'en' ? 'en-US' : 'fr-FR';
  const sectionsRef = useRef([]);
  const [formats, setFormats] = useState([]);
  const [currency, setCurrency] = useState('EUR');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  // Memes tarifs reels que la page Tarifs (GET /orders/formats) : jamais un
  // chiffre recopie en dur qui finirait par mentir le jour ou la grille
  // change. Sert au prix "des X €" du bandeau de confiance et aux 3 cartes
  // de formats plus bas.
  useEffect(() => {
    let annule = false;
    listPrintFormats()
      .then((data) => {
        if (annule) return;
        setFormats(data?.formats || []);
        setCurrency(data?.currency || 'EUR');
      })
      .catch(() => {});
    const onCountryChange = () => {
      listPrintFormats().then((data) => {
        if (annule) return;
        setFormats(data?.formats || []);
        setCurrency(data?.currency || 'EUR');
      }).catch(() => {});
    };
    window.addEventListener('bookipix:country-changed', onCountryChange);
    return () => {
      annule = true;
      window.removeEventListener('bookipix:country-changed', onCountryChange);
    };
  }, []);

  const formatPrix = (cents) => formatPriceCents(cents, currency, priceLocale);
  const cheapestPriceCents = formats.length
    ? Math.min(...formats.map((f) => f.startingPriceCents))
    : null;

  const steps = [
    { number: '01', title: t('steps.step1.title'), description: t('steps.step1.description') },
    { number: '02', title: t('steps.step2.title'), description: t('steps.step2.description') },
    { number: '03', title: t('steps.step3.title'), description: t('steps.step3.description') },
    { number: '04', title: t('steps.step4.title'), description: t('steps.step4.description') },
  ];

  return (
    <div className="home-luxe">
      {/* HERO */}
      <section ref={(el) => (sectionsRef.current[0] = el)} className="fade-in-section">
        <div className="container-luxe home-hero">
          <div>
            <span className="label-gold">{t('eventsSection.eyebrow')}</span>
            <h1>{t('hero.titleLine1')}<br />{t('hero.titleLine2')}</h1>
            <p className="lead" style={{ color: 'var(--text-light)', fontSize: '18px', maxWidth: '520px' }}>
              {t('hero.subtitle')}
            </p>

            <div className="home-hero-cta">
              <Link to="/create-book" className="btn btn-primary" style={{ padding: '16px 32px' }}>
                {t('hero.createCta')}
              </Link>
              <Link to="/how-it-works" className="btn btn-outline" style={{ padding: '16px 32px' }}>
                {t('hero.howItWorksCta')}
              </Link>
            </div>

            <ul className="home-hero-trust">
              <li><Icon name="check" />{cheapestPriceCents != null ? t('hero.trust.price', { price: formatPrix(cheapestPriceCents) }) : '\u00A0'}</li>
              <li><Icon name="check" />{t('hero.trust.pages')}</li>
              <li><Icon name="check" />{t('hero.trust.delivery')}</li>
            </ul>
          </div>

          <div className="home-hero-visual">
            <div className="home-hero-book">
              <img src="/images/exemples-stock/famille1.jpg" alt="" loading="lazy" />
              <img src="/images/exemples-stock/voyage1.jpg" alt="" loading="lazy" />
            </div>
            <div className="home-hero-tag is-a"><Icon name="eye" />{t('hero.visual.tagLive')}</div>
            <div className="home-hero-tag is-b"><Icon name="check" />{t('hero.visual.tagPrice')}</div>
          </div>
        </div>
      </section>

      {/* EVENEMENTS */}
      <section
        ref={(el) => (sectionsRef.current[1] = el)}
        className="fade-in-section section-luxe"
        style={{ backgroundColor: 'var(--silk)', padding: 'var(--space-xxl) 0', borderTop: 'var(--border-fine)', borderBottom: 'var(--border-fine)' }}
      >
        <div className="container-luxe">
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
            <span className="label-gold">{t('eventsSection.eyebrow')}</span>
            <h2>{t('eventsSection.title')}</h2>
            <p className="lead" style={{ margin: '0 auto' }}>{t('eventsSection.subtitle')}</p>
          </div>

          <div className="home-events-grid">
            {EVENT_ORDER.map((key) => (
              <Link to={`/create-book?event=${key}`} key={key} className="card home-event-card">
                <span className="home-event-icon"><Icon name={EVENT_ICONS[key]} /></span>
                <h3>{t(`events.${key}.title`)}</h3>
                <p>{t(`events.${key}.description`)}</p>
                <span className="home-event-discover">{t('eventsSection.discover')}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ALBUM COLLABORATIF */}
      <section ref={(el) => (sectionsRef.current[2] = el)} className="fade-in-section" style={{ padding: 'var(--space-xxl) 0' }}>
        <div className="container-luxe home-split">
          <div>
            <span className="label-gold">{t('collabSection.eyebrow')}</span>
            <h2>{t('collabSection.title')}</h2>
            <p className="lead">{t('collabSection.subtitle')}</p>
            <ul className="home-ticks">
              <li><Icon name="check" /><span>{t('collabSection.tick1')}</span></li>
              <li><Icon name="check" /><span>{t('collabSection.tick2')}</span></li>
              <li><Icon name="check" /><span>{t('collabSection.tick3')}</span></li>
            </ul>
            <div className="home-hero-cta">
              <Link to="/exemples" className="btn btn-outline">
                {t('collabSection.cta')}
              </Link>
            </div>
          </div>
          <div className="home-stack-photos">
            <img src="/images/exemples-stock/mariage-fete1.jpg" alt="" loading="lazy" style={{ left: 0, top: '20px', width: '56%', height: '44%', transform: 'rotate(-5deg)' }} />
            <img src="/images/exemples-stock/montagne1.jpg" alt="" loading="lazy" style={{ right: 0, top: 0, width: '50%', height: '48%', transform: 'rotate(4deg)' }} />
            <img src="/images/exemples-stock/bebe1.jpg" alt="" loading="lazy" style={{ left: '22%', bottom: 0, width: '58%', height: '46%', transform: 'rotate(-1deg)' }} />
          </div>
        </div>
      </section>

      {/* FORMATS */}
      {formats.length > 0 && (
        <section
          style={{ backgroundColor: 'var(--silk)', padding: 'var(--space-xxl) 0', borderTop: 'var(--border-fine)', borderBottom: 'var(--border-fine)' }}
        >
          <div className="container-luxe">
            <div style={{ marginBottom: 'var(--space-xl)' }}>
              <span className="label-gold">{t('formatsSection.eyebrow')}</span>
              <h2>{t('formatsSection.title')}</h2>
            </div>
            <div className="home-formats-grid">
              {formats.map((format) => {
                const isHard = format.reliure === 'rigide';
                const h = 72;
                const w = Math.round(h * (format.widthMm / format.heightMm));
                return (
                  <Link to="/tarifs" key={format.formatId} className={`card home-format-card${format.recommande ? ' is-recommended' : ''}`}>
                    {format.recommande && <span className="home-format-badge">{t('tarifs.recommended', { ns: 'legal', defaultValue: '★ Recommandé' })}</span>}
                    <div className="home-format-card-top">
                      <div>
                        <h3>{format.nom}</h3>
                        <p className="home-format-card-dims">{Math.round(format.widthMm / 10)} × {Math.round(format.heightMm / 10)} cm</p>
                      </div>
                      <span className={`home-format-shape${isHard ? ' is-hard' : ''}`} style={{ height: `${h}px`, width: `${w}px` }} />
                    </div>
                    <div className="home-format-card-price">
                      <small>{t('formatsSection.from')}</small>
                      {formatPrix(format.startingPriceCents)}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* COMMENT CA MARCHE (resume) */}
      <section ref={(el) => (sectionsRef.current[4] = el)} className="fade-in-section" style={{ padding: 'var(--space-xxl) 0' }}>
        <div className="container-luxe">
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
            <span className="label-gold">{t('processSection.eyebrow')}</span>
            <h2>{t('processSection.title')}</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-lg)' }}>
            {steps.map((step) => (
              <div key={step.number} className="card" style={{ padding: 'var(--space-xl) var(--space-lg)', textAlign: 'center' }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%', background: 'var(--gold)', color: 'var(--white)',
                  margin: '0 auto var(--space-md)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--font-primary)', fontSize: '18px', fontWeight: 700,
                }}>
                  {step.number}
                </div>
                <h3 style={{ fontSize: '18px', marginBottom: 'var(--space-sm)' }}>{step.title}</h3>
                <p className="body-text" style={{ color: 'var(--text-light)', margin: 0, fontSize: '14px' }}>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section ref={(el) => (sectionsRef.current[5] = el)} className="fade-in-section" style={{ padding: '0 0 var(--space-xxl)' }}>
        <div className="container-luxe">
          <div className="home-final">
            <div>
              <h2>{t('finalCta.title')}</h2>
              <p>{t('finalCta.subtitle')}</p>
            </div>
            <Link to="/create-book" className="btn btn-primary" style={{ padding: '16px 40px' }}>
              {t('finalCta.cta')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePageLuxe;
